import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { isEmpty, throttle, debounce } from 'lodash';
import { SearchField } from '@src/components/TextField';
import Checkbox from '@src/components/Checkbox';
import {
  OpeningSearchTagNameEnum,
  OpeningStateEnum,
  useOpeningsForTalentActivityFilterLazyQuery,
} from '@src/graphql/generated';
import { Props } from './props';
import './styles.scss';

const CONTAINER_MAX_HEIGHT = 400;
export const Positions: FC<Props> = ({ onPositionSelect, selectedPositions }) => {
  const [searchPattern, setSearchPattern] = useState<string>('');
  const positionsContainerRef = useRef<HTMLDivElement>(null);
  const [fetchPositions, { data, loading: isLoading, fetchMore, refetch }] =
    useOpeningsForTalentActivityFilterLazyQuery({
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    fetchPositions({
      variables: {
        paginationOptions: {
          limit: 10,
        },
        searchPatterns: searchPattern.length ? [{ tag: OpeningSearchTagNameEnum.TITLE, value: searchPattern }] : [],
        searchTags: [{ tag: OpeningSearchTagNameEnum.STATE, value: OpeningStateEnum.ACTIVE.toLowerCase() }],
      },
    });
  }, []);

  const openings = data?.openings.result;
  const pagination = data?.openings.pagination;

  const loadMorePositions = throttle(() => {
    const scrollHeight = positionsContainerRef?.current?.scrollHeight || 0;
    const scrollTop = positionsContainerRef?.current?.scrollTop || 0;

    if (!isLoading && pagination?.hasMoreResults && scrollHeight - scrollTop - CONTAINER_MAX_HEIGHT < 20) {
      fetchMore({
        updateQuery: (prevResult, { fetchMoreResult }) => {
          const prev = prevResult.openings;

          if (fetchMoreResult) {
            const { result: newResult, pagination: newPagination } = fetchMoreResult.openings;

            return {
              openings: {
                ...prev,
                pagination: {
                  ...prev.pagination,
                  hasMoreResults: newPagination.hasMoreResults,
                  maxCursor: newPagination.maxCursor,
                },
                result: [...prev.result, ...newResult],
              },
            };
          }

          return prevResult;
        },
        variables: {
          paginationOptions: {
            limit: 15,
            minCursor: pagination.maxCursor,
          },
          searchPatterns: searchPattern.length ? [{ pattern: searchPattern, tag: OpeningSearchTagNameEnum.TITLE }] : [],
          searchTags: [{ tag: OpeningSearchTagNameEnum.STATE, value: OpeningStateEnum.ACTIVE.toLowerCase() }],
        },
      });
    }
  }, 300);

  const triggerPositionsFetch = useCallback(
    debounce(searchPattern => {
      refetch({
        paginationOptions: {
          limit: 10,
        },
        searchPatterns: searchPattern.length ? [{ pattern: searchPattern, tag: OpeningSearchTagNameEnum.TITLE }] : [],
        searchTags: [{ tag: OpeningSearchTagNameEnum.STATE, value: OpeningStateEnum.ACTIVE.toLowerCase() }],
      });
    }, 300),
    [],
  );

  const handleSearchInputChange = (searchPattern: string): void => {
    setSearchPattern(searchPattern);
    triggerPositionsFetch(searchPattern);
  };

  return (
    <div className="filters-position">
      <div className="filters-position__title">
        FILTER BY POSITION {!!pagination?.totalCount && pagination?.totalCount}
      </div>
      <div className="filters-position__list">
        <SearchField
          className="filters-position__search"
          onChange={handleSearchInputChange}
          onClear={() => handleSearchInputChange('')}
          placeholder="Search..."
          value={searchPattern}
        />

        <div className="filters-position__list-wrapper">
          <div className="filters-position__list-item-header">
            <span>Open positions</span>
          </div>
          <div className="filters-position__list-container" onScroll={loadMorePositions} ref={positionsContainerRef}>
            {!isEmpty(openings) &&
              openings?.map(({ id, title }) => (
                <div className="filters-position__list-item" key={id}>
                  <Checkbox
                    label={title}
                    onChange={() => onPositionSelect(id)}
                    value={selectedPositions?.includes(id)}
                    wrapperClassName="filters-position__list-item-checkbox"
                  />
                </div>
              ))}
            {isLoading && <h5>Loading....</h5>}
          </div>
        </div>
      </div>
    </div>
  );
};
